import React, {
  RefObject,
  useContext,
  useRef,
} from 'react';
import pick from 'lodash/pick';
import values from 'lodash/values';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import PopulateButton from 'pages/Dashboard/components/Button';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import InsuranceLookup from 'pages/Dashboard/components/InsuranceLookup';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import InsurancePaymentForm from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/InsurancePaymentForm';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import Dropzone from 'pages/Dashboard/components/Dropzone';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  FileRejection,
} from 'react-dropzone';
import {
  useNotify,
} from 'core/hooks';
import PanelExtension from 'core/components/PanelExtension';
import DiscrepancySummaryCard from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/Discrepancy/DiscrepancySummaryCard';
import DiscrepancyDetailsPanel from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/Discrepancy/DiscrepancyDetailsPanel';

const requiredByDiscriminator: Readonly<Record<string, string[]>> = Object.freeze({
  ACHPayment: ['paymentAmount'],
  ManualCreditTransferPayment: ['paymentAmount'],
  CashPayment: ['paymentAmount'],
  CheckPayment: ['paymentAmount', 'transactionIdentifier'],
  CardPayment: ['paymentAmount', 'transactionIdentifier'],
  NonePayment: ['paymentAmount'],
});

export function InitialEOBStepTitle() {
  return <h3 className="text-lg font-semibold">Insurance Payment Information</h3>;
}

export default function InitialEOBStep() {
  const {
    toggleDoc,
    setHasEOB,
    setEOBFound,
    onClose,
    handleEOBDrop,
    isDiscrepancyDetailsOpen,
    isExtracted,
  } = useContext(WizardContext);
  const insuranceLookupRef = useRef<HTMLInputElement>(null);
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const [
    eobNumber,
    payer,
    insurancePayment,
    paymentDate,
    eobDoc,
  ] = watch([
    'eob.eobNumber',
    'payer',
    'insurancePayment.paymentMethod',
    'insurancePayment.paymentDate',
    'eobDoc',
  ]);
  const { discriminator } = insurancePayment ?? {};
  const paymentDateError = (
    errors.insurancePayment as { paymentDate?: unknown } | undefined
  )?.paymentDate;

  const insurancePaymentPresent = values(insurancePayment).every(isEmptyString)
    || values(pick(insurancePayment, requiredByDiscriminator[discriminator])).every(notEmptyString);

  const initialDataPresent = [eobNumber, payer?.payerName].every(notEmptyString)
    && insurancePaymentPresent
    && !isNil(paymentDate)
    && isNil(paymentDateError)
    && !isNil(discriminator);
  let paymentDateMessage: string | null = null;
  if (isNil(paymentDate)) {
    paymentDateMessage = 'Payment date is required';
  } else if (!isNil(paymentDateError)) {
    paymentDateMessage = 'Payment date is invalid';
  }
  const validationMessages = [
    !notEmptyString(eobNumber) ? 'EOB/check number is required' : null,
    !notEmptyString(payer?.payerName) ? 'Payer name is required' : null,
    !insurancePaymentPresent ? 'Payment details are required' : null,
    isNil(discriminator) ? 'Payment method is required' : null,
    paymentDateMessage,
  ].filter((message): message is string => !isNil(message));
  const nextTooltip = (
    <div className="flex flex-col gap-1 text-xs">
      <div className="font-semibold">Validation Failed</div>
      <div className="flex flex-col gap-1">
        {validationMessages.map((message) => (
          <div key={message}>{message}</div>
        ))}
      </div>
    </div>
  );

  const notify = useNotify();

  const handleDrop = (files: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      notify({
        message: 'This type of file is not supported.',
        variant: 'error',
      });
    } else {
      handleEOBDrop([], files[0]);
    }
  };

  return (
    <div className="relative flex min-h-0 w-full flex-1 overflow-hidden">
      <div className="flex min-h-0 w-[40rem] max-w-full flex-1 flex-col gap-form-row overflow-y-auto px-2">
        <Controller
          control={control}
          name="eob.eobNumber"
          render={({ field }) => (
            <div className="flex gap-2 items-end">
              <Input
                {...field}
                disabled={!isExtracted}
                className="flex-1"
                label="EOB / CheckOrEFTTrace Number"
                placeholder="Enter EOB Number"
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="payer"
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <InsuranceLookup
              {...field}
              required
              error={!isNil(error)}
              id="insurance-name"
              className="space-y-1"
              inputRef={insuranceLookupRef as RefObject<HTMLInputElement>}
            />
          )}
        />
        <div className="w-full max-h-[120px] overflow-y-hidden">
          {!isNil(eobDoc) ? (
            <MediaPreview
              media={eobDoc.media}
              onDelete={() => {
                URL.revokeObjectURL(eobDoc?.media?.url?.value!);
                setValue('eobDoc', null);
                toggleDoc(false);
              }}
              onUpdate={handleEOBDrop}
              mediaType={MultimediaType.EOB}
              className="w-full"
              thumbnail
            />
          ) : (
            <Dropzone
              onDrop={handleDrop}
              classes={{
                root: clsx(
                  'flex flex-col w-full p-8 justify-center items-center gap-3',
                  'border border-dashed border-blue-500 bg-blue-50 rounded',
                  'hover:bg-blue-100 cursor-pointer transition-colors',
                ),
                text: 'text-blue-500 text-sm font-semibold',
              }}
              placeholder="Drop EOB Document here or click to browse"
            />
          )}
        </div>
        <DiscrepancySummaryCard />
        <h3 className="text-xs font-semibold mt-2">Did you receive Insurance Payment?</h3>
        <InsurancePaymentForm
          classes={{
            root: 'gap-form-row',
            amount: '!min-w-0',
            discriminator: '!min-w-0',
            id: '!min-w-0',
            dynamicPanel: 'pt-1',
            ach: {
              root: 'grid grid-cols-1 sm:grid-cols-2 gap-form-field w-full',
              field: '!min-w-0 w-full',
            },
          }}
        />
        <div className="flex items-center justify-between mt-4">
          <PopulateButton
            variant="outlined"
            onClick={onClose}
          >
            Close
          </PopulateButton>
          <div className="flex items-center gap-3">
            {!isExtracted && (
              <PopulateButton
                variant="outlined"
                onClick={() => {
                  setHasEOB(false);
                  setEOBFound(false);
                }}
              >
                Back
              </PopulateButton>
            )}
            <ToolTip
              title={nextTooltip}
              hide={initialDataPresent}
            >
              <div>
                <PopulateButton
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const paymentDateIsValid = await trigger('insurancePayment.paymentDate');
                    if (paymentDateIsValid) {
                      setEOBFound(true);
                    }
                  }}
                  disabled={!initialDataPresent}
                >
                  Next
                </PopulateButton>
              </div>
            </ToolTip>
          </div>
        </div>
      </div>
      <PanelExtension
        className={clsx(
          'absolute right-0 top-0 h-full rounded-none transition-all duration-150 ease-in-out',
          {
            'w-0 bg-transparent opacity-0': !isDiscrepancyDetailsOpen,
            'w-[26rem] bg-white opacity-100': isDiscrepancyDetailsOpen,
          },
        )}
        classes={{
          container: clsx('h-full transition-opacity duration-150 ease-in-out border-l border-gray-200 ml-5', {
            'opacity-0': !isDiscrepancyDetailsOpen,
            'opacity-100': isDiscrepancyDetailsOpen,
          }),
        }}
      >
        <DiscrepancyDetailsPanel />
      </PanelExtension>
    </div>
  );
}
